import React, { useMemo, useState } from 'react';
import { SectionConfig, ObjectType } from '../../types/metadata';
import { Badge, ObjectTypeBadge } from '../shared/Badge';
import { SectionModal } from './SectionModal';
import { ConfirmDialog } from '../shared/ConfirmDialog';
import { EmptyState } from '../shared/EmptyState';
import { Edit, Trash2, ChevronUp, ChevronDown, Search, Plus, Layers } from 'lucide-react';
interface SectionListProps {
  sections: SectionConfig[];
  getFieldCount: (key: string) => number;
  onAdd: (section: Omit<SectionConfig, 'id'>) => void;
  onUpdate: (id: string, updates: Partial<SectionConfig>) => void;
  onDelete: (id: string) => void;
  onReorder: (id: string, direction: 'up' | 'down') => void;
}
export function SectionList({
  sections,
  getFieldCount,
  onAdd,
  onUpdate,
  onDelete,
  onReorder
}: SectionListProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterObject, setFilterObject] = useState<ObjectType | 'All'>('All');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSection, setEditingSection] = useState<SectionConfig | undefined>();
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const filteredSections = useMemo(() => {
    return sections.filter(s => filterObject === 'All' || s.objectType === filterObject).filter(s => s.sectionLabel.toLowerCase().includes(searchTerm.toLowerCase()) || s.sectionKey.toLowerCase().includes(searchTerm.toLowerCase())).sort((a, b) => a.order - b.order);
  }, [sections, searchTerm, filterObject]);
  const handleEdit = (section: SectionConfig) => {
    setEditingSection(section);
    setIsModalOpen(true);
  };
  const handleSave = (data: any) => {
    if (editingSection) {
      onUpdate(editingSection.id, data);
    } else {
      onAdd(data);
    }
  };
  return <div className="space-y-4" data-id="element-345">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row justify-between gap-3 bg-white p-4 rounded-xl shadow-sm border border-gray-200" data-id="element-346">
        <div className="flex flex-1 gap-3" data-id="element-347">
          <div className="relative max-w-sm flex-1" data-id="element-348">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-349">
              <Search className="h-4 w-4 text-gray-400" data-id="element-350" />
            </div>
            <input type="text" placeholder="Search sections..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder:text-gray-400" data-id="element-351" />
          </div>
          <select value={filterObject} onChange={e => setFilterObject(e.target.value as any)} className="block w-40 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-352">
            <option value="All" data-id="element-353">All Objects</option>
            {Object.values(ObjectType).map(type => <option key={type} value={type} data-id="element-354">
                {type}
              </option>)}
          </select>
        </div>
        <button onClick={() => {
        setEditingSection(undefined);
        setIsModalOpen(true);
      }} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors" data-id="element-355">
          <Plus className="h-4 w-4 mr-2" data-id="element-356" />
          New Section
        </button>
      </div>

      {/* Table */}
      <div className="bg-white shadow-sm rounded-xl border border-gray-200 overflow-hidden" data-id="element-357">
        {filteredSections.length > 0 ? <div className="overflow-x-auto" data-id="element-358">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-359">
              <thead className="bg-gray-50" data-id="element-360">
                <tr data-id="element-361">
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-24" data-id="element-362">
                    Order
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-363">
                    Section Details
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-364">
                    Target Object
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-365">
                    Fields
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-20" data-id="element-366">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 w-24" data-id="element-367">
                    <span className="sr-only" data-id="element-368">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100" data-id="element-369">
                {filteredSections.map((section, index) => {
              const fieldCount = getFieldCount(section.sectionKey);
              return <tr key={section.id} className="hover:bg-blue-50/40 transition-colors group" data-id="element-370">
                      <td className="px-6 py-3.5 whitespace-nowrap" data-id="element-371">
                        <div className="flex items-center gap-1.5" data-id="element-372">
                          <span className="text-sm font-medium text-gray-900 w-6 tabular-nums" data-id="element-373">
                            {section.order}
                          </span>
                          <div className="flex flex-col" data-id="element-374">
                            <button onClick={() => onReorder(section.id, 'up')} disabled={index === 0} className="text-gray-300 hover:text-blue-600 disabled:opacity-20 disabled:hover:text-gray-300 transition-colors p-0.5" data-id="element-375">
                              <ChevronUp className="h-3.5 w-3.5" data-id="element-376" />
                            </button>
                            <button onClick={() => onReorder(section.id, 'down')} disabled={index === filteredSections.length - 1} className="text-gray-300 hover:text-blue-600 disabled:opacity-20 disabled:hover:text-gray-300 transition-colors p-0.5" data-id="element-377">
                              <ChevronDown className="h-3.5 w-3.5" data-id="element-378" />
                            </button>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-3.5" data-id="element-379">
                        <div className="flex flex-col" data-id="element-380">
                          <span className="text-sm font-medium text-gray-900" data-id="element-381">
                            {section.sectionLabel}
                          </span>
                          <span className="text-xs text-gray-400 font-mono mt-0.5" data-id="element-382">
                            {section.sectionKey}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-3.5 whitespace-nowrap" data-id="element-383">
                        <ObjectTypeBadge type={section.objectType} data-id="element-384" />
                      </td>
                      <td className="px-6 py-3.5 whitespace-nowrap" data-id="element-385">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 tabular-nums" data-id="element-386">
                          {fieldCount} field{fieldCount !== 1 ? 's' : ''}
                        </span>
                      </td>
                      <td className="px-6 py-3.5 whitespace-nowrap" data-id="element-387">
                        <button onClick={() => onUpdate(section.id, {
                    active: !section.active
                  })} className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${section.active ? 'bg-green-500' : 'bg-gray-300'}`} aria-label={section.active ? 'Deactivate' : 'Activate'} data-id="element-388">
                          <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${section.active ? 'translate-x-4' : 'translate-x-0'}`} data-id="element-389" />
                        </button>
                      </td>
                      <td className="px-6 py-3.5 whitespace-nowrap text-right" data-id="element-390">
                        <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-391">
                          <button onClick={() => handleEdit(section)} className="text-gray-400 hover:text-blue-600 p-1.5 rounded-lg hover:bg-blue-50 transition-colors" title="Edit" data-id="element-392">
                            <Edit className="h-4 w-4" data-id="element-393" />
                          </button>
                          <button onClick={() => setDeleteId(section.id)} className="text-gray-400 hover:text-red-600 p-1.5 rounded-lg hover:bg-red-50 transition-colors" title="Delete" data-id="element-394">
                            <Trash2 className="h-4 w-4" data-id="element-395" />
                          </button>
                        </div>
                      </td>
                    </tr>;
            })}
              </tbody>
            </table>
          </div> : <EmptyState icon={Layers} title="No sections found" description={searchTerm ? 'Try adjusting your search or filters.' : 'Get started by creating your first form section.'} action={!searchTerm ? <button onClick={() => {
        setEditingSection(undefined);
        setIsModalOpen(true);
      }} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-blue-600 hover:bg-blue-700 transition-colors" data-id="element-397">
                  <Plus className="h-4 w-4 mr-2" data-id="element-398" />
                  Create Section
                </button> : undefined} data-id="element-396" />}
      </div>

      <SectionModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onSave={handleSave} initialData={editingSection} data-id="element-399" />

      <ConfirmDialog isOpen={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={() => deleteId && onDelete(deleteId)} title="Delete Section" message="Are you sure you want to delete this section? This action cannot be undone." confirmLabel="Delete Section" data-id="element-400" />
    </div>;
}